import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  TextField,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock as LockIcon,
  Security,
  ArrowForward,
  Gavel as DisclaimerIcon,
  OpenInNew as OpenInNewIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const formatRoleLabel = (role) => {
  if (!role) return 'locsy admin';
  if (role === 'franchisee_admin' || role === 'net_center_admin' || role === 'business_admin') {
    return 'locsy admin';
  }
  return role.replace(/_/g, ' ');
};

const Profile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManageBillingUpi = ['app_admin', 'super_admin', 'franchisee_admin', 'net_center_admin', 'business_admin'].includes(user?.role);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [mobileOtp, setMobileOtp] = useState('');
  const [mobileOtpSent, setMobileOtpSent] = useState(false);
  const [mobileOtpLoading, setMobileOtpLoading] = useState(false);
  const [mobileOtpMessage, setMobileOtpMessage] = useState('');
  const [mobileVerificationEligible, setMobileVerificationEligible] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

  /** Always use /api root even if REACT_APP_API_URL ends with /franchise or /franchisee */
  const getApiRoot = () => {
    const raw = String(API_BASE_URL || 'http://localhost:3000/api').trim().replace(/\/$/, '');
    return raw.replace(/\/franchisee?$/i, '') || 'http://localhost:3000/api';
  };

  const isVyaparProfile = ['franchisee_admin', 'net_center_admin', 'business_admin'].includes(user?.role);
  const profileDisplayName = isVyaparProfile
    ? (user?.business_name || user?.shop_name || user?.full_name || 'Vyapar business')
    : (user?.full_name || 'Admin User');
  const profileAuthConfig = () => ({
    headers: {
      Authorization: `Bearer ${localStorage.getItem('admin_token') || localStorage.getItem('token') || ''}`,
    },
  });

  useEffect(() => {
    if (!isVyaparProfile) {
      setMobileVerificationEligible(false);
      return undefined;
    }
    let active = true;
    axios.get(`${getApiRoot()}/franchisee/admin/me`, profileAuthConfig())
      .then((response) => {
        if (active) setMobileVerificationEligible(Boolean(response.data?.data?.mobile_verification_eligible));
      })
      .catch(() => {
        if (active) setMobileVerificationEligible(false);
      });
    return () => { active = false; };
  }, [isVyaparProfile]);

  const sendProfileMobileOtp = async () => {
    setMobileOtpLoading(true);
    setMobileOtpMessage('');
    try {
      const response = await axios.post(
        `${getApiRoot()}/franchisee/admin/profile/mobile-verification/send-otp`,
        {},
        profileAuthConfig(),
      );
      setMobileOtpSent(true);
      setMobileOtp('');
      setMobileOtpMessage(response.data?.message || 'OTP sent to your registered mobile.');
    } catch (error) {
      setMobileOtpMessage(error.response?.data?.message || 'Failed to send OTP.');
    } finally {
      setMobileOtpLoading(false);
    }
  };

  const verifyProfileMobileOtp = async () => {
    if (!/^\d{6}$/.test(mobileOtp)) {
      setMobileOtpMessage('Enter the 6-digit OTP.');
      return;
    }
    setMobileOtpLoading(true);
    setMobileOtpMessage('');
    try {
      const response = await axios.post(
        `${getApiRoot()}/franchisee/admin/profile/mobile-verification/verify-otp`,
        { otp: mobileOtp },
        profileAuthConfig(),
      );
      setMobileOtpSent(false);
      setMobileOtp('');
      setMobileOtpMessage(response.data?.message || 'Mobile number verified.');
    } catch (error) {
      setMobileOtpMessage(error.response?.data?.message || 'OTP verification failed.');
    } finally {
      setMobileOtpLoading(false);
    }
  };

  const handlePasswordChange = (field) => (e) => {
    setPasswordData({
      ...passwordData,
      [field]: e.target.value,
    });
    setPasswordError('');
    setPasswordSuccess('');
  };

  const handleTogglePasswordVisibility = (field) => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field],
    });
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');
    setLoading(true);

    // Validation
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordError('All fields are required');
      setLoading(false);
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      setLoading(false);
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New password and confirm password do not match');
      setLoading(false);
      return;
    }

    try {
      const isFranchiseeDashboardUser = [
        'franchisee_admin',
        'net_center_admin',
        'business_admin',
      ].includes(user?.role);

      const apiRoot = getApiRoot();
      const payload = {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      };

      const franchiseeUrls = isFranchiseeDashboardUser
        ? [
            `${apiRoot}/franchisee/admin/change-password`,
            `${apiRoot}/auth/franchisee-admin/change-password`,
            `${apiRoot}/franchise/change-password`,
          ]
        : [`${apiRoot}/users/${user.id}/change-password`];

      let response = null;
      let lastError = null;
      for (const url of franchiseeUrls) {
        try {
          response = await axios.post(url, payload);
          break;
        } catch (err) {
          lastError = err;
          const status = err.response?.status;
          if (status === 404) continue;
          throw err;
        }
      }
      if (!response) {
        throw lastError || new Error('Password change endpoint not found');
      }

      if (response.data.success) {
        setPasswordSuccess('Password changed successfully!');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
        setTimeout(() => {
          setOpenPasswordDialog(false);
          setPasswordSuccess('');
        }, 2000);
      }
    } catch (error) {
      setPasswordError(
        error.response?.data?.message || 'Failed to change password. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom>
        My Profile
      </Typography>
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar
                sx={{
                  width: 120,
                  height: 120,
                  mx: 'auto',
                  mb: 2,
                  bgcolor: 'primary.main',
                  fontSize: 48,
                }}
              >
                {profileDisplayName.charAt(0) || 'A'}
              </Avatar>
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                {profileDisplayName}
              </Typography>
              <Chip
                label={formatRoleLabel(user?.role)}
                color="primary"
                sx={{ mb: 2 }}
              />
              <Typography variant="body2" color="text.secondary">
                {user?.email || 'No email'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user?.phone}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Profile Information
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    {isVyaparProfile ? 'Business Name' : 'Full Name'}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {profileDisplayName}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Phone Number
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {user?.phone}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {user?.email || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Role
                  </Typography>
                  <Chip
                    label={formatRoleLabel(user?.role)}
                    color="primary"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    City
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {user?.city || 'N/A'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Location Radius
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {user?.radius_km || 5} km
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        {canManageBillingUpi && (
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Billing UPI Setup
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Configure the default UPI ID used for bill QR and payment collection.
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<SettingsIcon />}
                    endIcon={<ArrowForward />}
                    onClick={() => navigate('/bills/admin-controls')}
                  >
                    Open Admin Controls
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}        {isVyaparProfile && mobileVerificationEligible && (
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Mobile verification</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Verify the registered mobile by OTP. UPI updates also require OTP verification.
                </Typography>
                {mobileOtpMessage && (
                  <Alert severity={mobileOtpMessage.toLowerCase().includes('verified') ? 'success' : 'info'} sx={{ mb: 2 }}>
                    {mobileOtpMessage}
                  </Alert>
                )}
                {mobileOtpSent && (
                  <TextField
                    label="6-digit OTP"
                    value={mobileOtp}
                    onChange={(event) => setMobileOtp(String(event.target.value || '').replace(/\D/g, '').slice(0, 6))}
                    inputProps={{ inputMode: 'numeric', maxLength: 6, autoComplete: 'one-time-code' }}
                    sx={{ mr: 1, mb: { xs: 1, sm: 0 } }}
                  />
                )}
                <Button variant="contained" onClick={mobileOtpSent ? verifyProfileMobileOtp : sendProfileMobileOtp} disabled={mobileOtpLoading}>
                  {mobileOtpLoading ? 'Please wait…' : mobileOtpSent ? 'Verify mobile OTP' : 'Send mobile OTP'}
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Security Settings
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<LockIcon />}
                  onClick={() => setOpenPasswordDialog(true)}
                >
                  Change Password
                </Button>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Keep your account secure by regularly updating your password.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Session Management
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    View and manage all your active sessions across different devices and locations.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<Security />}
                  endIcon={<ArrowForward />}
                  onClick={() => navigate('/profile/sessions')}
                >
                  Manage Sessions
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Legal & Terms
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    View our terms of service, privacy policy, disclaimer, and legal information.
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    startIcon={<DisclaimerIcon />}
                    endIcon={<OpenInNewIcon />}
                    onClick={() => navigate('/privacy-policy')}
                  >
                    Privacy Policy
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<DisclaimerIcon />}
                    endIcon={<OpenInNewIcon />}
                    onClick={() => navigate('/disclaimer')}
                  >
                    View Disclaimer
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Password Change Dialog */}
      <Dialog open={openPasswordDialog} onClose={() => !loading && setOpenPasswordDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPasswordError('')}>
              {passwordError}
            </Alert>
          )}
          {passwordSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {passwordSuccess}
            </Alert>
          )}
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Current Password"
              type={showPasswords.current ? 'text' : 'password'}
              value={passwordData.currentPassword}
              onChange={handlePasswordChange('currentPassword')}
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => handleTogglePasswordVisibility('current')}
                      edge="end"
                    >
                      {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="New Password"
              type={showPasswords.new ? 'text' : 'password'}
              value={passwordData.newPassword}
              onChange={handlePasswordChange('newPassword')}
              margin="normal"
              helperText="Must be at least 6 characters"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => handleTogglePasswordVisibility('new')}
                      edge="end"
                    >
                      {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label="Confirm New Password"
              type={showPasswords.confirm ? 'text' : 'password'}
              value={passwordData.confirmPassword}
              onChange={handlePasswordChange('confirmPassword')}
              margin="normal"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => handleTogglePasswordVisibility('confirm')}
                      edge="end"
                    >
                      {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPasswordDialog(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleChangePassword}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Changing...' : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Profile;

